import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { CustomSlider, SvgIcons } from "@tripian/react";
import useTranslate from "../../hooks/useTranslate";
import classes from "./StepTimeframe.module.scss";

interface IStepTimeframe {
  dayIndex: number;
  plansIdDateWithTimeframe: {
    id: number;
    date: string;
    startTime: string;
    endTime: string;
  }[];
  defaultTimeframe: { startTime: string; endTime: string };
  callbackStepTimeframe: (
    timeFrame: { startTime: string; endTime: string },
    minDayIndex: number,
    planId: number
  ) => void;
}

const hourRange = [...Array(24)].map((_, i) => String(i).padStart(2, "0"));
const minuteRange = ["00", "30"];
const timeRange = hourRange.flatMap(h => minuteRange.map(m => `${h}:${m}`));

const StepTimeframe: React.FC<IStepTimeframe> = ({
  dayIndex,
  plansIdDateWithTimeframe,
  defaultTimeframe,
  callbackStepTimeframe,
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState<number>(
    plansIdDateWithTimeframe[dayIndex].id
  );
  const [timeframe, setTimeframe] = useState({ startTime: "", endTime: "" });
  const [editMode, setEditMode] = useState(false);
  const { t } = useTranslate();

  useEffect(() => {
    if (!editMode) {
      setTimeframe(defaultTimeframe);
    }
  }, [defaultTimeframe, editMode]);

  useEffect(() => {
    if (editMode) {
      const plan = plansIdDateWithTimeframe.find(p => p.id === selectedPlanId);
      if (plan) {
        setTimeframe({ startTime: plan.startTime, endTime: plan.endTime });
      }
    }
  }, [selectedPlanId, plansIdDateWithTimeframe, editMode]);

  const getTimeIndex = (time: string) => timeRange.findIndex(t => t === time);

  const handleStartTimeChange = (value: string) => {
    const endIndex = getTimeIndex(timeframe.endTime);
    const startIndex = getTimeIndex(value);
    const newEndIndex = startIndex + 1;
    const newEndTime = timeRange[newEndIndex] || timeframe.endTime;

    if (endIndex <= startIndex) {
      setTimeframe({ startTime: value, endTime: newEndTime });
    } else {
      setTimeframe(prev => ({ ...prev, startTime: value }));
    }
  };

  const handleEndTimeChange = (value: string) => {
    setTimeframe(prev => ({ ...prev, endTime: value }));
  };

  const filteredEndTimes = useMemo(() => {
    const startIndex = getTimeIndex(timeframe.startTime);
    return timeRange.slice(startIndex + 1);
  }, [timeframe.startTime]);

  const isUpdateDisabled = useMemo(() => {
    const original = plansIdDateWithTimeframe.find(
      p => p.id === selectedPlanId
    );
    if (!original) return true;
    return (
      timeframe.startTime === original.startTime &&
      timeframe.endTime === original.endTime
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, plansIdDateWithTimeframe]);

  const handleUpdate = () => {
    callbackStepTimeframe(timeframe, dayIndex, selectedPlanId);
    setEditMode(false);
  };

  const handleCancel = () => {
    const plan = plansIdDateWithTimeframe.find(p => p.id === selectedPlanId);
    if (plan) {
      setTimeframe({ startTime: plan.startTime, endTime: plan.endTime });
    }
    setEditMode(false);
  };

  const handleOpenModal = () => {
    const plan = plansIdDateWithTimeframe[dayIndex];
    setSelectedPlanId(plan.id);
    setTimeframe({ startTime: plan.startTime, endTime: plan.endTime });
    setEditMode(true);
  };

  return (
    <>
      <div className={classes.readonlyDisplay} onClick={handleOpenModal}>
        <div className={classes.readonlyTimeInputs}>
          <span>{timeframe.startTime}</span>
          <SvgIcons.ChevronRight size="16" />
          <span>{timeframe.endTime}</span>
        </div>
      </div>

      {editMode &&
        createPortal(
          <div className={classes.modalBackdrop}>
            <div className={classes.modalBox}>
              <div className={classes.modalHeader}>
                <div className="text-2xl font-semibold">
                  {t("trips.myTrips.itinerary.updateTripHours")}
                </div>
                <span className={classes.modalClose} onClick={handleCancel}>
                  <SvgIcons.X />
                </span>
              </div>

              <div className={classes.section}>
                <div className={classes.label}>
                  1. {t("trips.myTrips.itinerary.customPoiModal.selectADay")}*
                </div>
                <div className={classes.sliderContainer}>
                  <CustomSlider scrollSize={150}>
                    {plansIdDateWithTimeframe.map((planIdDate, index) => (
                      <div
                        key={planIdDate.id}
                        className={`${classes.dayButton} ${selectedPlanId === planIdDate.id ? classes.dayButtonActive : ""}`}
                        onClick={() => setSelectedPlanId(planIdDate.id)}
                      >
                        <span>{`${t("trips.myTrips.itinerary.day")} ${index + 1}`}</span>
                        {selectedPlanId === planIdDate.id && (
                          <span className={classes.dayDate}>
                            {" "}
                            | {moment(planIdDate.date).format("MMM DD")}
                          </span>
                        )}
                      </div>
                    ))}
                  </CustomSlider>
                </div>
              </div>

              <div className={classes.section}>
                <div className={classes.label}>
                  2. {t("trips.createNewTrip.form.destination.hours")}*
                </div>
                <div className={classes.timeContent}>
                  <select
                    className={classes.select}
                    value={timeframe.startTime}
                    onChange={e => handleStartTimeChange(e.target.value)}
                  >
                    <option value="">-- --</option>
                    {timeRange.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>

                  <SvgIcons.ChevronRight stroke="#000" />

                  <select
                    className={classes.select}
                    value={timeframe.endTime}
                    onChange={e => handleEndTimeChange(e.target.value)}
                  >
                    <option value="">-- --</option>
                    {filteredEndTimes.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                className={classes.stepTimesButton}
                onClick={handleUpdate}
                disabled={isUpdateDisabled}
              >
                {t("update")}
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default StepTimeframe;
